/**
 * <StudentRankingPage> — « Classement » de l'élève (page).
 *
 * L'écran d'aujourd'hui, rhabillé : ses deux classements (« Général », « Classe de 3ème »), chacun
 * une liste (rang, avatar, nom affiché, titre, XP), la ligne de l'élève mise en avant, et sa ligne
 * en pied sous trois points quand il est classé plus loin que ce que la liste montre. Pas de
 * podium, et aucune ligne ne presse, aujourd'hui non plus.
 *
 * Sur un téléphone, les deux onglets d'aujourd'hui sont deux pilules de sélection : un classement
 * à la fois. Au large, il n'y a plus rien à choisir : les deux se posent côte à côte, chacun sous
 * son nom. Un élève sans classe n'a que le classement général, sans pilule.
 *
 * Tant qu'il n'a aucun XP, le classement est fermé : « Débloque le classement ! », avec l'en-tête
 * et sa croix (l'ancien écran n'en avait pas, on n'en sortait que par le retour du téléphone), et
 * sans champion : un écran vide n'en porte pas.
 *
 * On y arrive depuis « Mon compte » (la ligne « Classements »).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { CardList } from "../../molecules/CardList"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { EmptyState } from "../../molecules/EmptyState"
import { RankingGap, RankingRow } from "../../molecules/RankingRow"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { RANKING_SIDE_BY_SIDE_FROM, rankingLayoutStyles as styles } from "./rankingLayout"
import { StudentRankingSkeleton } from "./StudentRankingSkeleton"
import { type UIRankingBoard } from "../../../ports/primary/ui/types"

export type { UIRankingBoard }

export type StudentRankingPageProps = {
    /** Les classements de l'élève. `null` tant qu'ils ne sont pas là. */
    boards: UIRankingBoard[] | null
    /** Le classement ouvert sur un téléphone. Défaut : le premier. */
    activeKey?: string
    onSelectBoard?: (key: string) => void
    /** L'élève n'a encore aucun XP : le classement est fermé. Défaut `false`. */
    locked?: boolean
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** La croix de l'en-tête. */
    onClose: () => void
    /** Une relecture est en cours : la flèche de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    testID?: string
}

const TEXTS = {
    title: "Classement",
    lockedTitle: frenchSpacing("Débloque le classement !"),
    lockedMessage: "Réalise ta première activité pour te lancer dans la compétition",
}

const noop = () => {}

function Board(props: { board: UIRankingBoard; titled: boolean }) {
    const { board, titled } = props
    const { label, entries, me } = board

    const inList = me != null && entries.some((entry) => entry.id === me.id)
    // Les trois points ne disent que des rangs sautés : classé juste après la liste, il la suit.
    const hasGap = me != null && !inList && me.rank - 1 > entries.length

    return (
        <Stack gap="2">
            {titled && <SectionLabel>{label}</SectionLabel>}
            <CardList>
                {entries.map((entry) => (
                    <RankingRow key={entry.id} entry={entry} highlighted={me != null && entry.id === me.id} />
                ))}
                {hasGap && <RankingGap />}
                {me != null && !inList && <RankingRow entry={me} highlighted />}
            </CardList>
        </Stack>
    )
}

export function StudentRankingPage(props: StudentRankingPageProps) {
    const {
        boards,
        activeKey,
        onSelectBoard = noop,
        locked = false,
        loading = false,
        error,
        onRetry,
        onClose,
        refreshing = false,
        onRefresh,
        testID,
    } = props

    const { onLayout, atLeast: sideBySide } = useWidthAtLeast(RANKING_SIDE_BY_SIDE_FROM)

    const header = (
        <ClosableHeader
            title={TEXTS.title}
            onClose={onClose}
            refreshing={refreshing}
            // Un classement fermé n'a rien à relire.
            onRefresh={locked ? undefined : onRefresh}
        />
    )

    const body = () => {
        if (locked) {
            return <EmptyState iconName="signal" title={TEXTS.lockedTitle} message={TEXTS.lockedMessage} />
        }
        if (boards == null || boards.length === 0) return null

        const several = boards.length > 1
        const all = sideBySide && several
        const active = boards.find((board) => board.key === activeKey) ?? boards[0]

        // Un seul arbre pour les deux dispositions, et tous les classements montés d'emblée : la page
        // se mesure après son premier rendu, et un classement monté (ou remonté) après coup ne
        // repeint pas toujours un avatar déjà chargé ailleurs. Sur un téléphone, celui qui n'est pas
        // ouvert est seulement retiré de l'affichage : changer de pilule ne recharge rien.
        return (
            <Stack gap="4" style={all ? undefined : styles.single}>
                {several && !all && (
                    <View accessibilityRole="radiogroup" accessibilityLabel={TEXTS.title} style={styles.pills}>
                        {boards.map((board) => (
                            <View key={board.key} style={styles.pill}>
                                <ChoiceChip
                                    variant="pill"
                                    fullWidth
                                    label={board.label}
                                    selected={board.key === active.key}
                                    onPress={() => onSelectBoard(board.key)}
                                />
                            </View>
                        ))}
                    </View>
                )}
                <View style={all ? styles.boards : undefined}>
                    {boards.map((board) => (
                        <View
                            key={board.key}
                            style={[
                                all ? styles.board : undefined,
                                !all && board.key !== active.key ? pageStyles.hidden : undefined,
                            ]}
                        >
                            <Board board={board} titled={all} />
                        </View>
                    ))}
                </View>
            </Stack>
        )
    }

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={locked ? undefined : onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<StudentRankingSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <View onLayout={onLayout} style={styles.fullWidth}>
                {body()}
            </View>
        </PageTemplate>
    )
}

const pageStyles = StyleSheet.create(() => ({
    /** Sur un téléphone, le classement qui n'est pas ouvert reste monté, hors de l'affichage. */
    hidden: { display: "none" },
}))
